import { Suspense, useEffect, type ReactElement } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { syncSupportChat } from "@/lib/support-chat";
import { lazyWithRetry } from "@/utils/lazyWithRetry";

// Eager imports — needed for first paint or wrap every protected route.
// Anything else is lazyWithRetry below so it ships as its own chunk and only
// downloads when the user navigates to that route. We use lazyWithRetry
// instead of React.lazy so users with the previous index.html cached survive
// a Vercel deploy that rotated the chunk hashes — see `utils/lazyWithRetry.ts`.
import { AuthProvider } from "./contexts/AuthContext";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import BusinessRoute from "./components/BusinessRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { LanguageSync } from "./i18n/LanguageSync";
import { ProfileLanguageSync } from "./i18n/ProfileLanguageSync";
import { RequireFeature } from "@/components/subscription/RequireFeature";

// Auth and CommandPalette are lazy because they're not on the critical
// path for first paint:
//   - Auth is only hit when the user clicks Sign In / Get Started
//   - CommandPalette only renders behind the ⌘K shortcut
// Lazy-loading them trims their cmdk/lucide/supabase-auth deps from the
// landing-page entry bundle — ~30 KB gzip off first-load JS for SEO
// crawlers and cold visitors who never click into the app.
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const CommandPalette = lazyWithRetry(() =>
  import("./components/ds/command-palette").then((m) => ({ default: m.CommandPalette }))
);

// ── Dashboard pages (largest cluster, only loaded post-login) ───────────────
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Technicians = lazyWithRetry(() => import("./pages/Technicians"));
const Customers = lazyWithRetry(() => import("./pages/Customers"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const Branding = lazyWithRetry(() => import("./pages/Branding"));
const PublicIntake = lazyWithRetry(() => import("./pages/PublicIntake"));
const PublicEstimate = lazyWithRetry(() => import("./pages/PublicEstimate"));
const Requests = lazyWithRetry(() => import("./pages/Requests"));
const Estimates = lazyWithRetry(() => import("./pages/Estimates"));
const EstimateEditor = lazyWithRetry(() => import("./pages/EstimateEditor"));
// Calendar + LocationHistory + PerformanceMonitoring each pull large vendor
// deps (FullCalendar / Leaflet / Recharts). Splitting them is the single
// biggest initial-JS win.
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const Jobs = lazyWithRetry(() => import("./pages/Jobs"));
const Equipment = lazyWithRetry(() => import("./pages/Equipment"));
const AiReceptionist = lazyWithRetry(() => import("./pages/AiReceptionist"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Billing = lazyWithRetry(() => import("./pages/Billing"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const AccountSetup = lazyWithRetry(() => import("./pages/AccountSetup"));
const InviteAccept = lazyWithRetry(() => import("./pages/InviteAccept"));
const Integrations = lazyWithRetry(() => import("./pages/Integrations"));
const Invoices = lazyWithRetry(() => import("./pages/Invoices"));
const PayInvoice = lazyWithRetry(() => import("./pages/PayInvoice"));
const TrackJob = lazyWithRetry(() => import("./pages/TrackJob"));
const ReferralLanding = lazyWithRetry(() => import("./pages/referral/ReferralLanding"));
const PartnersLanding = lazyWithRetry(() => import("./pages/partners/PartnersLanding"));
const PartnerPortal = lazyWithRetry(() => import("./pages/partners/PartnerPortal"));
const ActivateRedirect = lazyWithRetry(() => import("./pages/partners/ActivateRedirect"));
const LocationTracking = lazyWithRetry(() => import("./pages/LocationTracking"));
const LocationHistory = lazyWithRetry(() =>
  import("./pages/LocationHistory").then((m) => ({ default: m.LocationHistory }))
);
const PerformanceMonitoring = lazyWithRetry(() =>
  import("./pages/PerformanceMonitoring").then((m) => ({ default: m.PerformanceMonitoring }))
);
// Dev-only tools. The `import.meta.env.DEV ? … : null` keeps the dynamic
// import in a dead branch in production, so Rollup tree-shakes the chunks out
// entirely (not just the routes).
const SeedTestData = import.meta.env.DEV
  ? lazyWithRetry(() => import("./pages/SeedTestData"))
  : null;
const DSPreview = import.meta.env.DEV
  ? lazyWithRetry(() => import("./pages/DSPreview"))
  : null;
const TechnicianDashboard = lazyWithRetry(() => import("./pages/TechnicianDashboard"));
const Suspended = lazyWithRetry(() => import("./pages/Suspended"));
const GoogleCalendarCallback = lazyWithRetry(() => import("./pages/GoogleCalendarCallback"));

// ── Legal + help pages (rarely visited; lazy) ───────────────────────────────
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const LiabilityDisclaimers = lazyWithRetry(() => import("./pages/LiabilityDisclaimers"));
const HelpQuickBooks = lazyWithRetry(() => import("./pages/HelpQuickBooks"));
const Status = lazyWithRetry(() => import("./pages/Status"));

// ── SEO/marketing pages (prerendered at build for crawlers; for live nav
//    they should still lazy-load instead of bloating the app shell) ──────────
const Blog = lazyWithRetry(() => import("./pages/seo/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/seo/BlogPost"));
const AuthorPage = lazyWithRetry(() => import("./pages/seo/AuthorPage"));
const VsJobber = lazyWithRetry(() => import("./pages/seo/VsJobber"));
const VsHousecallPro = lazyWithRetry(() => import("./pages/seo/VsHousecallPro"));
const VsServiceTitan = lazyWithRetry(() => import("./pages/seo/VsServiceTitan"));
const CustomerApp = lazyWithRetry(() => import("./pages/seo/CustomerApp"));
const HvacSoftware = lazyWithRetry(() => import("./pages/seo/HvacSoftware"));
const PlumbingSoftware = lazyWithRetry(() => import("./pages/seo/PlumbingSoftware"));
const ElectricalSoftware = lazyWithRetry(() => import("./pages/seo/ElectricalSoftware"));
const CityLanding = lazyWithRetry(() => import("./pages/seo/CityLanding"));
const FeatureScheduling = lazyWithRetry(() => import("./pages/seo/FeatureScheduling"));
const FeatureGPSTracking = lazyWithRetry(() => import("./pages/seo/FeatureGPSTracking"));
const FeatureInvoicing = lazyWithRetry(() => import("./pages/seo/FeatureInvoicing"));
const PricingPage = lazyWithRetry(() => import("./pages/seo/PricingPage"));
const DemoPage = lazyWithRetry(() => import("./pages/seo/DemoPage"));

const queryClient = new QueryClient();

function RouteLoader() {
  return (
    // data-route-loader: main.tsx polls for this marker's absence before
    // firing prerender-ready, so prerendered HTML never captures the
    // Suspense fallback instead of the real page.
    <div data-route-loader className="flex h-screen w-full items-center justify-center text-muted-foreground">
      Loading…
    </div>
  );
}

// Public, crawlable routes. We declare each one twice — once at its canonical
// English path and once under `/es/...` — so Google can index both languages
// and the Vite prerender plugin can statically render either side.
const PUBLIC_ROUTES: Array<{ path: string; element: ReactElement }> = [
  { path: "/", element: <Index /> },
  { path: "/auth", element: <Auth /> },
  { path: "/terms", element: <TermsOfService /> },
  { path: "/privacy", element: <PrivacyPolicy /> },
  { path: "/disclaimers", element: <LiabilityDisclaimers /> },
  { path: "/help/quickbooks", element: <HelpQuickBooks /> },
  { path: "/status", element: <Status /> },
  { path: "/blog", element: <Blog /> },
  { path: "/blog/:slug", element: <BlogPost /> },
  { path: "/authors/cristian-machado", element: <AuthorPage /> },
  { path: "/vs-jobber", element: <VsJobber /> },
  { path: "/vs-housecall-pro", element: <VsHousecallPro /> },
  { path: "/vs-servicetitan", element: <VsServiceTitan /> },
  { path: "/customer-app", element: <CustomerApp /> },
  { path: "/hvac-scheduling-software", element: <HvacSoftware /> },
  { path: "/plumbing-software", element: <PlumbingSoftware /> },
  { path: "/electrical-contractor-software", element: <ElectricalSoftware /> },
  { path: "/hvac-software/:city", element: <CityLanding /> },
  { path: "/features/scheduling", element: <FeatureScheduling /> },
  { path: "/features/gps-tracking", element: <FeatureGPSTracking /> },
  { path: "/features/invoicing", element: <FeatureInvoicing /> },
  { path: "/pricing", element: <PricingPage /> },
  { path: "/demo", element: <DemoPage /> },
  { path: "/partners", element: <PartnersLanding /> },
];

function localizedPath(path: string): string {
  return path === "/" ? "/es" : `/es${path}`;
}

/**
 * El chat de soporte no aparece en las superficies del partner: ya llevan su
 * propio card de ayuda y el widget se les sentaba encima. Ver lib/support-chat.
 * Aqui se cubre la navegacion dentro del SPA; la carga inicial la decide
 * index.html.
 */
function SupportChatVisibility() {
  const { pathname } = useLocation();
  useEffect(() => {
    syncSupportChat(pathname);
  }, [pathname]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <AuthProvider>
      <BrowserRouter>
        <LanguageSync />
        <SupportChatVisibility />
        <ProfileLanguageSync />
        <CommandPalette />
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            {PUBLIC_ROUTES.flatMap((r) => [
              <Route key={`en:${r.path}`} path={r.path} element={r.element} />,
              <Route key={`es:${r.path}`} path={localizedPath(r.path)} element={r.element} />,
            ])}

            {/* Auth / onboarding utility routes (not crawled, no /es mirror) */}
            <Route path="/auth/callback/google" element={<GoogleCalendarCallback />} />
            <Route path="/account/setup" element={<AccountSetup />} />
            <Route path="/invite/accept" element={<InviteAccept />} />
            <Route path="/pay/:invoiceId" element={<PayInvoice />} />
            {/* Public live-tracking page for the en-route email. The token is
                jobs.tracking_token — a capability token, same trust model as
                /pay/:invoiceId. No /es mirror: the page picks its language
                from ?lang= and then from the customer's preferred_language. */}
            <Route path="/track/:token" element={<TrackJob />} />
            {/* Public estimate request form. Printed on flyers and QR stickers,
                so the slug is guessable by design — see get-public-intake-form. */}
            <Route path="/q/:slug" element={<PublicIntake />} />
            {/* The customer's estimate. The token IS the capability — no
                account, no login, same model as the pay link. */}
            <Route path="/e/:token" element={<PublicEstimate />} />
            {/* Flyer QR target. Standalone (not prerendered/crawlable) — it
                only validates the code and redirects into signup. Mirrored
                under /es so a Spanish scanner keeps their language. */}
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/es/r/:code" element={<ReferralLanding />} />
            {/* Partner portal + code activation. `/socio` is bookmarkable and
                carries no credential: it renders the login (email → 6-digit
                code) until there is a partner session. The old
                /socio/{token} route is gone rather than redirected —
                production has never had a single partner, and leaving it
                alive would keep a capability URL working for no one. */}
            <Route path="/socio" element={<PartnerPortal />} />
            <Route path="/es/socio" element={<PartnerPortal />} />
            <Route path="/activar/:code" element={<ActivateRedirect />} />
            <Route path="/es/activar/:code" element={<ActivateRedirect />} />
            <Route path="/onboarding" element={
              <BusinessRoute>
                <Onboarding />
              </BusinessRoute>
            } />
            <Route path="/technician-info" element={<TechnicianDashboard />} />
            <Route path="/suspended" element={<Suspended />} />

            {/* Dashboard routes — language driven by stored preference, not URL */}
            <Route path="/dashboard" element={
              <BusinessRoute>
                <DashboardLayout />
              </BusinessRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="technicians" element={<Technicians />} />
              <Route path="customers" element={<Customers />} />
              <Route path="services" element={<Services />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="equipment" element={<Equipment />} />
              {/* Registered in every environment. Who may see it is decided at
                  runtime by the ai_addon flag on the subscription, which the
                  page itself enforces with <FeatureGate fallback={<NotFound/>}>
                  — a business without the add-on gets the same 404 as any other
                  unknown path and never learns the feature exists. A build-time
                  hostname constant could only ever answer "which deploy is
                  this", which stopped being the question once the add-on became
                  sellable per business. */}
              <Route path="ai" element={<AiReceptionist />} />
              <Route path="location" element={<LocationTracking />} />
              <Route path="location-history" element={<LocationHistory />} />
              <Route path="performance" element={<PerformanceMonitoring />} />
              {/* Dev-only: seeds fake data. Never shipped to stage/prod. */}
              {SeedTestData && (
                <Route path="seed-test-data" element={<SeedTestData />} />
              )}
              <Route path="profile" element={<Profile />} />
              <Route path="billing" element={<Billing />} />
              <Route path="integrations" element={<Integrations />} />
              {/* Wrapped, not conditionally registered: the estimates grant is
                  per business and resolves at runtime, so unlike the AI
                  receptionist above there is no build-time constant to branch
                  on. The sidebar hiding these was never enough — typing the
                  URL loaded the whole page. */}
              <Route
                path="requests"
                element={<RequireFeature feature="estimates"><Requests /></RequireFeature>}
              />
              <Route
                path="estimates"
                element={<RequireFeature feature="estimates"><Estimates /></RequireFeature>}
              />
              <Route
                path="estimates/:id"
                element={<RequireFeature feature="estimates"><EstimateEditor /></RequireFeature>}
              />
              <Route
                path="branding"
                element={<RequireFeature feature="estimates"><Branding /></RequireFeature>}
              />
              <Route path="invoices" element={<Invoices />} />
            </Route>

            {/* Internal staff admin lives at admin.workloop.us. Any hit to
                /admin or /admin-login here is 308-redirected by vercel.json
                before the SPA loads, so no route is declared in this app. */}

            {/* Dev-only design-system playground. Was publicly reachable with
                no auth in prod; gate it out of deployed builds. */}
            {DSPreview && (
              <Route path="/ds-preview" element={<DSPreview />} />
            )}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
